import type { AssetJoined } from './AssetInventory';
import type { RoleDefinition } from '../../../app/roles';
import type { PresenterDepth } from '../../../domain';
import { MaskedValue } from '../phone-detail/MaskedValue';

type AssetTableProps = {
  assets: AssetJoined[];
  onSelect: (id: string) => void;
  onAction: (message: string) => void;
  onExport: () => void;
  role: RoleDefinition;
  depth: PresenterDepth;
};

export function AssetTable({ assets, onSelect, onAction, onExport, role, depth }: AssetTableProps) {

  // A helper to calculate a simulated lifecycle percentage (0-100) based on purchase date (mocked)
  const getLifecycleProgress = (asset: AssetJoined) => {
    if (!asset.purchaseDate) return 0;
    if (asset.id === 'TEL-0318') return 80;
    if (asset.id === 'MOB-0418') return 95;
    return 40;
  };
  const columnCount = depth === 'overview' ? 4 : depth === 'architecture' ? 5 : 6;

  return (
    <div className="table-container">
      <div className="bulk-actions">
        <button
          className="button"
          disabled={!role.canEditTechnicalRecords}
          onClick={() => onAction(`Requested a simulated MDM check for ${assets.length} visible devices.`)}
          title={!role.canEditTechnicalRecords ? "Technical II access is required" : ""}
          type="button"
        >
          Ping MDM
        </button>
        <button
          className="button button--danger"
          disabled={!role.canApproveSensitiveChanges}
          onClick={() => onAction("Remote wipe requires confirmation; no device data was changed.")}
          title={!role.canApproveSensitiveChanges ? "Department Lead approval is required" : ""}
          type="button"
        >
          Remote Wipe
        </button>
        <button className="button" onClick={onExport} type="button">Export Audit</button>
      </div>
      <table className="asset-table">
        <thead>
          <tr>
            {depth === 'overview' && (
              <>
                <th className="frozen-col">Device</th>
                <th>Custodian</th>
                <th>Attention state</th>
                <th>Location</th>
              </>
            )}
            {depth === 'operations' && (
              <>
                <th className="frozen-col">Asset Tag</th>
                <th>Type</th>
                <th>Assignee</th>
                <th>Status / MDM</th>
                <th>Lifecycle</th>
                <th>Location</th>
              </>
            )}
            {depth === 'architecture' && (
              <>
                <th className="frozen-col">Record ID</th>
                <th>Asset / permitted identifier</th>
                <th>Assignment edge</th>
                <th>Location edge</th>
                <th>Record state</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {!assets.length && (
            <tr>
              <td className="asset-empty-state" colSpan={columnCount}>
                <strong>No assets match this view.</strong>
                <span>Change the saved view or clear the asset filter.</span>
              </td>
            </tr>
          )}
          {assets.map(asset => (
            <tr key={asset.id} className="asset-row">
              {depth === 'overview' && (
                <>
                  <td className="frozen-col" data-label="Device">
                    <button className="asset-record-link" onClick={() => onSelect(asset.id)} type="button">{asset.name}</button>
                    <div className="subtext">{asset.assetTag}</div>
                  </td>
                  <td data-label="Custodian">{asset.assigneeName ? <><strong>{asset.assigneeName}</strong><div className="subtext">{asset.departmentName}</div></> : <span className="subtext">Available pool</span>}</td>
                  <td data-label="Attention state"><span className={`status-badge status-${asset.state}`}>{asset.isCompliant ? 'Ready' : 'Needs follow-up'}</span></td>
                  <td data-label="Location">{asset.locationName || <span className="subtext">Location not recorded</span>}</td>
                </>
              )}
              {depth === 'operations' && (
                <>
                  <td className="frozen-col" data-label="Asset tag">
                    <button className="asset-record-link" onClick={() => onSelect(asset.id)} type="button">{asset.assetTag}</button>
                    <div className="subtext">{asset.serialNumber || 'No serial'}</div>
                  </td>
                  <td data-label="Type">{asset.name}</td>
                  <td data-label="Assignee">
                    {asset.assigneeName ? (
                      <div className="assignee-badge">
                        <div className="avatar" title={`Assigned to ${asset.assigneeName}`}>{(asset.assigneeName[0] || 'U')}</div>
                        <div><span>{asset.assigneeName}</span><div className="subtext">{asset.departmentName}</div></div>
                      </div>
                    ) : <span className="subtext">Unassigned</span>}
                  </td>
                  <td data-label="Status / MDM">
                    <span className={`status-badge status-${asset.state}`}>{asset.state}</span>
                    {asset.isCompliant !== undefined && (
                      <span className={`mdm-badge mdm-${asset.isCompliant ? 'compliant' : 'warning'}`}>{asset.isCompliant ? 'Verified' : 'Non-Compliant'}</span>
                    )}
                  </td>
                  <td data-label="Lifecycle">
                    <div className="lifecycle-bar-container" title="3-Year Lifecycle Progress">
                      <div className="lifecycle-bar" style={{ width: `${getLifecycleProgress(asset)}%`, backgroundColor: getLifecycleProgress(asset) > 90 ? 'var(--critical-default)' : 'var(--accent-default)' }} />
                    </div>
                  </td>
                  <td data-label="Location">{asset.locationName || <span className="subtext">--</span>}</td>
                </>
              )}
              {depth === 'architecture' && (
                <>
                  <td className="frozen-col" data-label="Record ID"><code>{asset.id}</code></td>
                  <td data-label="Asset / permitted identifier">
                    <button className="asset-record-link" onClick={() => onSelect(asset.id)} type="button">{asset.assetTag}</button>
                    <div className="subtext"><MaskedValue value={asset.serialNumber || 'No serial'} role={role} type="serial" /></div>
                  </td>
                  <td data-label="Assignment edge"><strong>{asset.assigneeName || 'No active assignment'}</strong><div className="subtext">{asset.departmentName || 'Unassigned'} · person relationship</div></td>
                  <td data-label="Location edge"><strong>{asset.locationName || 'No room link'}</strong><div className="subtext">asset.locationId → room.id</div></td>
                  <td data-label="Record state"><span className={`status-badge status-${asset.state}`}>{asset.state}</span><div className="subtext">Repository-backed record</div></td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
